import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Heart, Shield, CheckCircle2, Star, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const LandingEmotional = () => {
  const [showMore, setShowMore] = useState(false);

  const testimonials = [
    {
      name: "Rachel",
      location: "Arizona, USA",
      story: "My teenage daughter was struggling with depression. I didn't know how else to help her. Having a prayer delivered to the Garden Tomb felt like releasing my burden to a higher power. She's doing so much better now.",
      highlight: "She's doing so much better now"
    },
    {
      name: "Thomas",
      location: "California, USA", 
      story: "I sent a prayer along the Via Dolorosa for my brother who was battling addiction. My brother is now 8 months clean.",
      highlight: "8 months clean"
    },
    {
      name: "Maria",
      location: "Ontario, Canada",
      story: "I used this service to pray for reconciliation with my estranged sister. We hadn't spoken in five years. We're rebuilding our relationship now, one conversation at a time.",
      highlight: "We're rebuilding our relationship"
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Your Prayer Delivered to Jerusalem's Holiest Sites"
        description="Let your deepest prayers reach the sacred ground of Jerusalem. Personal prayer delivery with photo proof. Join thousands who found hope."
        path="/welcome"
      />
      
      {/* Hero Section - Emotional Focus */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587297069400-6cc5da81658a)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-5 py-20 text-center">
          {/* Trust Badge */}
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full backdrop-blur-sm">
            <Heart className="w-4 h-4 text-amber-400" fill="currentColor" />
            <span className="text-white text-sm font-medium">Trusted by thousands of faithful souls</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
            Your Prayer<br />
            <span className="text-amber-400 italic">Deserves to Reach</span><br />
            Sacred Ground
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Whatever weighs on your heart — healing, guidance, or hope — <br className="hidden md:block" />
            let it be carried to Jerusalem's holiest sites.
          </p>
          
          {/* Main CTA */}
          <Link to="/prayer-request">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-10 py-7 shadow-2xl hover:shadow-amber-500/25 transition-all transform hover:scale-105" data-testid="hero-cta-btn">
              <Send className="mr-3" size={22} />
              Send My Prayer Now
            </Button>
          </Link>
          
          <p className="mt-4 text-gray-300 text-sm">
            Starting at $29 • Photo proof included
          </p>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </div>
        </div>
      </section>

      {/* Emotional Hook Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg md:text-xl text-amber-600 font-medium mb-4">
              We understand...
            </p>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-8 leading-tight">
              Some burdens are too heavy<br />to carry alone
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Whether you're praying for a loved one's healing, seeking guidance through a difficult season, 
              or longing for peace in chaos — your prayer matters. And it deserves to reach the most 
              sacred places on Earth.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works - Simple */}
      <section className="py-16 md:py-20 bg-stone-100">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-12">
              A Simple, Sacred Process
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Write Your Prayer", desc: "Pour out your heart in your own words. It stays completely private." },
                { step: "2", title: "We Deliver It", desc: "Our Jerusalem messengers carry your prayer to the holy site you choose." },
                { step: "3", title: "Receive Proof", desc: "Get timestamped photos from the Holy Land." }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Story Focus */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-4">
              Real Stories of Hope
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From people just like you
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-amber-400" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    "{t.story}"
                  </p>
                  <p className="text-amber-600 font-semibold mb-3">
                    "{t.highlight}"
                  </p>
                  <div className="border-t border-stone-200 pt-3">
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-12 bg-stone-100">
        <div className="container mx-auto px-5">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-gray-700">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="font-medium">Secure Payments</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="font-medium">Photo Proof Guaranteed</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Heart className="w-5 h-5 text-green-600" />
              <span className="font-medium">100% Confidential</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-gray-900 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587297069400-6cc5da81658a)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container mx-auto px-5 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6">
              Your prayer is waiting<br />
              <span className="text-amber-400">to reach holy ground</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Don't let another day pass with your burden unspoken.<br />
              Let Jerusalem carry it for you.
            </p>
            
            <Link to="/prayer-request">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-12 py-7 shadow-2xl transition-all transform hover:scale-105" data-testid="final-cta-btn">
                <Send className="mr-3" size={22} />
                Send My Prayer to Jerusalem
              </Button>
            </Link>
            
            <p className="mt-6 text-gray-400 text-sm">
              Delivered weekly • Photo proof included • Starting at $29
            </p>
          </div>
        </div>
      </section>

      {/* Minimal Footer for Landing */}
      <footer className="py-6 bg-gray-950 text-center">
        <p className="text-gray-500 text-sm">
          © 2025 Jerusalem Holy Prayer • <Link to="/privacy" className="hover:text-gray-300">Privacy</Link> • <Link to="/terms" className="hover:text-gray-300">Terms</Link>
        </p>
      </footer>
    </div>
  );
};

export default LandingEmotional;
